import Card from "@/components/Card";
import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import { SkeletonResume } from "@/components/SkeletonResume";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { listSemester } from "@/constants";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { formatDateIndonesia } from "@/lib/utils";
import {
  getBiodata,
  getListBerkasPenerimaan,
  getListBerkasPortofolio,
  getListDataPilihanProdi,
  getListNilaiRaporPenerimaan,
  getListPrestasi,
} from "@/services/getData";
import {
  DataBerkas,
  DataNilaiRapor,
  DataPilihanProdi,
  DataPrestasi,
  DataSiswa,
  SysBerkasPortofolio,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  Award,
  BookOpen,
  Briefcase,
  Eye,
  FileText,
  GraduationCap,
  Info,
  Loader2,
  MapPin,
  Save,
  Trophy,
  User,
  X,
} from "lucide-react"; // Gunakan Lucide Icons
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

const postDataFinalisasi = async ({
  id_sys_periode,
  nomor_utbk_snpmb,
}: {
  id_sys_periode: string;
  nomor_utbk_snpmb: string;
}) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/validasi/finalisasi",
    {
      id_sys_periode,
      nomor_utbk_snpmb,
    },
  );
  return response.data;
};

const postDataUnfinalisasi = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/validasi/unfinalisasi",
    {
      id_sys_periode,
    },
  );
  return response.data;
};

export default function Resume() {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { user } = useUser();

  const [isAcceptTerm, setIsAcceptTerm] = useState<boolean | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationUnfinalisasi, setShowConfirmationUnfinalisasi] =
    useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [showPortofolio, setShowPortofolio] = useState(false);
  const [showRapor, setShowRapor] = useState(false);
  const [showPrestasi, setShowPrestasi] = useState(false);
  // const [showDetailPrestasi, setShowDetailPrestasi] = useState(false); // Tidak digunakan di layout baru
  const [selectedBerkas, setSelectedBerkas] = useState<DataBerkas | null>(null);
  const [selectedSysBerkasPortofolio, setSelectedSysBerkasPortofolio] =
    useState<SysBerkasPortofolio | null>(null);
  const [selectedNilaiRapor, setSelectedNilaiRapor] =
    useState<DataNilaiRapor | null>(null);
  const [selectedDataPrestasi, setSelectedDataPrestasi] =
    useState<DataPrestasi | null>(null);

  const [form, setForm] = useState({
    nomor_utbk_snpmb: "",
  });

  const { dataPeriode, timePassedActive } = useTimePeriode();
  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();

  // ... Queries (Sama seperti sebelumnya, disingkat untuk fokus UI) ...
  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
    });
  const { data: listDataPilihanProdi, isPending: isPendingDataPilihanProdi } =
    useQuery({
      queryKey: ["listDataPilihanProdi", sessionPeriode?.id_sys_periode],
      queryFn: () =>
        getListDataPilihanProdi(sessionPeriode?.id_sys_periode || ""),
    });
  const { data: listBerkas } = useQuery({
    queryKey: ["listDataBerkasPenerimaan", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });
  const { data: listNilaiRapor } = useQuery({
    queryKey: ["listNilaiRapor", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListNilaiRaporPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });
  const { data: listDataPrestasi } = useQuery({
    queryKey: ["listPrestasi"],
    queryFn: () => getListPrestasi(sessionPeriode?.id_sys_periode || ""),
  });
  const { data: listBerkasPortofolio } = useQuery({
    queryKey: ["listBerkasPortofolio", sessionPeriode?.id_sys_periode],
    queryFn: () => getListBerkasPortofolio(sessionPeriode?.id_sys_periode),
  });

  const [listNilaiRaporFormated, setListNilaiRaporFormated] = useState<
    DataNilaiRapor[]
  >((listSemester as DataNilaiRapor[]) || []);

  const listBerkasPortofolioOlahraga =
    listBerkasPortofolio?.filter((i: any) => i?.bidang === "olahraga") || [];
  const listBerkasPortofolioSeni =
    listBerkasPortofolio?.filter((i: any) => i?.bidang === "seni") || [];

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: postDataFinalisasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Data berhasil difinalisasi.");
      setShowConfirmation(false);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(
        error.response?.data?.message || "Gagal melakukan finalisasi.",
      );
    },
  });

  const handleSimpanPermanen = () => {
    mutate({
      id_sys_periode: sessionPeriode?.id_sys_periode,
      nomor_utbk_snpmb: form.nomor_utbk_snpmb,
    });
  };

  const { mutate: mutateUnfinalisasi, isPending: isPendingUnfinalisasi } =
    useMutation({
      mutationFn: postDataUnfinalisasi,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["dataStep"] });
        toast.success(data?.message || "Finalisasi berhasil dibatalkan.");
        setShowConfirmationUnfinalisasi(false);
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast.error(
          error.response?.data?.message || "Gagal membatalkan finalisasi.",
        );
      },
    });

  const handleBatalFinalisasi = () => {
    mutateUnfinalisasi({
      id_sys_periode: sessionPeriode?.id_sys_periode,
    });
  };

  useEffect(() => {
    if (!listNilaiRapor || listNilaiRapor?.length === 0) return;
    const merged = listSemester.map((semesterItem) => {
      const nilaiRapor = listNilaiRapor.find(
        (r: DataNilaiRapor) => r.semester === semesterItem.semester,
      );
      return { ...semesterItem, ...nilaiRapor };
    });
    setListNilaiRaporFormated(merged);
  }, [listNilaiRapor]);

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  // Helper Component for Info Row
  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0 text-sm">
      <span className="text-slate-500 font-medium">{label}</span>
      <span className="font-semibold text-slate-900 dark:text-white text-right mt-1 sm:mt-0">
        {value || "-"}
      </span>
    </div>
  );

  if (isPendingDataSiswa || isPendingDataPilihanProdi)
    return <SkeletonResume />;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Rangkuman Pendaftaran
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Pastikan seluruh data Anda sudah benar sebelum melakukan finalisasi.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* KOLOM KIRI: BIODATA & PROFIL */}
        <div className="lg:col-span-1 space-y-6">
          {/* Card Foto & Nama */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col items-center text-center">
            <div className="w-32 h-40 rounded-lg overflow-hidden border-2 border-slate-100 dark:border-slate-700 shadow-md mb-4 bg-slate-50">
              {dataSiswa?.path_foto ? (
                <img
                  src={dataSiswa.path_foto}
                  alt="Foto Peserta"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                  No Foto
                </div>
              )}
            </div>
            <h3 className="font-bold text-lg text-slate-900 dark:text-white leading-tight">
              {dataSiswa?.nama_lengkap}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-3">
              {dataSiswa?.nisn}
            </p>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
            >
              Jalur {dataPeriode?.jalur_jenis?.nama_jalur_jenis}
            </Badge>
          </div>

          {/* Card Detail Biodata */}
          <Card className="p-5">
            <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Biodata Diri
            </h4>
            <InfoRow
              label="Jenis Kelamin"
              value={
                dataSiswa?.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"
              }
            />
            <InfoRow
              label="TTL"
              value={`${dataSiswa?.tempat_lahir}, ${
                dataSiswa?.tanggal_lahir
                  ? formatDateIndonesia(dataSiswa.tanggal_lahir, "medium")
                  : ""
              }`}
            />
            <div className="py-2 border-b border-slate-100 dark:border-slate-800 last:border-0 text-sm">
              <span className="text-slate-500 font-medium block mb-1">
                Alamat Domisili
              </span>
              <p className="text-slate-900 dark:text-white font-medium">
                {dataSiswa?.jalan_domisili}
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-xs mt-0.5">
                {dataSiswa?.nama_kecamatan}, {dataSiswa?.nama_kabupaten},{" "}
                {dataSiswa?.nama_provinsi}
              </p>
            </div>
          </Card>
        </div>

        {/* KOLOM KANAN: DATA AKADEMIK & BERKAS */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pilihan Prodi */}
          <Card className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
              <GraduationCap className="w-5 h-5 text-purple-600" /> Pilihan
              Program Studi
            </h3>
            <div className="space-y-3">
              {listDataPilihanProdi?.map(
                (item: DataPilihanProdi, idx: number) => (
                  <div
                    key={item.id_data_pilihan_prodi}
                    className="flex items-center p-3 rounded-lg border border-slate-100 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-purple-700 font-bold text-xs mr-3 dark:bg-purple-900 dark:text-purple-300">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-800 dark:text-slate-200">
                        {item.unitkerja?.nama}
                      </p>
                      {item.pilihan_lokasi_kuliah && (
                        <Badge variant="secondary" className="mt-1 text-[10px]">
                          {item.pilihan_lokasi_kuliah}
                        </Badge>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Card>

          {/* Nilai Rapor */}
          <Card className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
              <BookOpen className="w-5 h-5 text-blue-600" /> Nilai Rapor
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {listNilaiRaporFormated.map((rapor, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-900 text-center flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => {
                    if (rapor.file_rapor_semester) {
                      setShowRapor(true);
                      setSelectedNilaiRapor(rapor);
                    }
                  }}
                >
                  <span className="text-[10px] text-slate-500 uppercase font-bold mb-1">
                    Sem {rapor.semester}
                  </span>
                  <span className="text-xl font-bold text-slate-800 dark:text-white">
                    {rapor.rata_rata_nilai || "-"}
                  </span>
                  {rapor.file_rapor_semester ? (
                    <Badge className="mt-2 text-[9px] h-4 bg-emerald-500 hover:bg-emerald-600">
                      File Ada
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="mt-2 text-[9px] h-4 text-slate-400 border-dashed"
                    >
                      Kosong
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Data Prestasi */}
          <Card className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
              <Trophy className="w-5 h-5 text-amber-500" /> Data Prestasi
            </h3>
            {listDataPrestasi && listDataPrestasi.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {listDataPrestasi.map((pres: DataPrestasi) => (
                  <div
                    key={pres.id_data_prestasi}
                    className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex items-start gap-3.5 hover:bg-slate-100/50 dark:hover:bg-slate-900 transition-colors"
                  >
                    <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-500 rounded-full shrink-0">
                      <Award className="w-4 h-4" />
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-slate-900 dark:text-slate-100 line-clamp-1">
                          {pres.jenis_prestasi?.nama_jenis_prestasi}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {pres.jenjang_prestasi?.nama_jenjang_prestasi} •{" "}
                          {pres.tahun_prestasi}
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 font-medium">
                          {pres.capaian_prestasi?.nama_capaian_prestasi}
                        </p>
                      </div>

                      {/* UI/UX Bukti Prestasi */}
                      {pres.path_bukti_prestasi && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-blue-600"
                          onClick={() => {
                            setShowPrestasi(true);
                            setSelectedDataPrestasi(pres);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">
                Tidak ada data prestasi yang diinput.
              </p>
            )}
          </Card>

          {/* Berkas & Portofolio */}
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Berkas Wajib */}
            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
                <Briefcase className="w-5 h-5 text-emerald-600" /> Berkas
                Pendaftaran
              </h3>
              <ul className="grid grid-cols-1 space-y-2">
                {listBerkas?.listDataBerkasSiswa?.map((berkas: DataBerkas) => (
                  <li
                    key={berkas.id_data_berkas_siswa}
                    className="flex items-center justify-between text-sm p-2 rounded bg-slate-50 dark:bg-slate-800/50"
                  >
                    <span className="truncate pr-2">
                      {berkas.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-blue-600"
                      onClick={() => {
                        setShowPdf(true);
                        setSelectedBerkas(berkas);
                      }}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Portofolio */}
            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2">
                <MapPin className="w-5 h-5 text-pink-600" /> Portofolio
              </h3>
              <ul className="space-y-2">
                {[
                  ...listBerkasPortofolioOlahraga,
                  ...listBerkasPortofolioSeni,
                ].map((porto: SysBerkasPortofolio) => (
                  <li
                    key={porto.id_sys_berkas_portofolio}
                    className="flex items-center justify-between text-sm p-2 rounded bg-slate-50 dark:bg-slate-800/50"
                  >
                    <span className="truncate pr-2">
                      {porto.nama_jenis_berkas}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-blue-600"
                      onClick={() => {
                        setShowPortofolio(true);
                        setSelectedSysBerkasPortofolio(porto);
                      }}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
                {!listBerkasPortofolioOlahraga.length &&
                  !listBerkasPortofolioSeni.length && (
                    <li className="text-xs text-slate-400 italic">
                      Tidak ada portofolio yang diwajibkan.
                    </li>
                  )}
              </ul>
            </Card>
          </div>

          {/* UTBK Input */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-amber-500" /> Info UTBK (Opsional)
            </h4>
            <div className="space-y-2">
              <Label>Nomor Peserta SNBT {dataPeriode?.tahun}</Label>
              <Input
                maxLength={12}
                value={form.nomor_utbk_snpmb}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nomor_utbk_snpmb: e.target.value.replace(/\D/g, ""),
                  }))
                }
                placeholder="Contoh: 241234567890"
                disabled={isDisableInput}
              />
            </div>
            <p className="mt-2 italic text-xs">
              Isi jika Anda mengikuti UTBK SNBT tahun {dataPeriode?.tahun}.
              Kosongkan jika tidak mengikuti.
            </p>
          </div>

          {/* Final Action */}
          {!isDisableInput && (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
              <Button
                size="lg"
                className="w-full font-bold text-base h-12"
                onClick={() => setShowConfirmation(true)}
              >
                <Save className="w-5 h-5 mr-2" /> Simpan Permanen (Finalisasi)
              </Button>
              <p className="text-center text-xs text-slate-500 mt-2">
                Pastikan semua data sudah benar. Data tidak dapat diubah setelah
                finalisasi.
              </p>
            </div>
          )}

          {/* Batal Finalisasi Action */}
          {isSudahFinalisasi && !isValidasiPendaftaran ? (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
              <Button
                variant="destructive"
                size="lg"
                className="w-full font-bold text-base h-12"
                onClick={() => setShowConfirmationUnfinalisasi(true)}
              >
                <X className="w-5 h-5 mr-2" /> Batal Finalisasi
              </Button>
              <p className="text-center text-xs text-slate-500 mt-2">
                Anda dapat membatalkan finalisasi untuk mengubah data kembali
                sebelum divalidasi oleh panitia.
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      {/* --- MODALS (Confirmation, PDF, Rapor, dll) --- */}
      {/* Modal Konfirmasi */}
      <Modal
        show={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setIsAcceptTerm(false);
        }}
        className="lg:w-[40vw] w-[95vw]"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Konfirmasi Finalisasi
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Anda akan melakukan validasi akhir pada data pendaftaran. <br />
              <strong>
                Data tidak dapat diubah kembali setelah proses ini.
              </strong>
            </p>
          </div>

          <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-100 dark:border-red-900 mb-6">
            <div className="flex gap-3 items-start">
              <Checkbox
                id="confirm-check"
                className="mt-0.5 border-red-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                checked={isAcceptTerm == true}
                onCheckedChange={() => setIsAcceptTerm((prev) => !prev)}
              />
              <Label
                htmlFor="confirm-check"
                className="text-sm cursor-pointer text-slate-800 dark:text-slate-200 leading-relaxed font-medium"
              >
                Saya menyatakan seluruh data sudah benar dan saya menyetujui
                data yang disimpan permanen tidak dapat diubah kembali dengan
                alasan apa pun.
              </Label>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowConfirmation(false)}
              disabled={isPending}
            >
              Batal
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleSimpanPermanen}
              disabled={isPending || !isAcceptTerm}
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Ya, Finalisasi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Konfirmasi Batal Finalisasi */}
      <Modal
        show={showConfirmationUnfinalisasi}
        onClose={() => setShowConfirmationUnfinalisasi(false)}
        className="lg:w-[45vw] w-[95vw]"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Konfirmasi Batal Finalisasi
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Anda akan membatalkan finalisasi data pendaftaran. <br />
              <strong>
                Anda dapat mengubah kembali data pendaftaran setelah proses ini,
                namun Anda harus melakukan finalisasi ulang sebelum batas waktu
                berakhir.
              </strong>
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowConfirmationUnfinalisasi(false)}
              disabled={isPendingUnfinalisasi}
            >
              Kembali
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleBatalFinalisasi}
              disabled={isPendingUnfinalisasi}
            >
              {isPendingUnfinalisasi ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Ya, Batal Finalisasi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showPdf}
        onClose={() => {
          setShowPdf(false);
          // Optional: clear selected setelah delay animasi
          setTimeout(() => setSelectedBerkas(null), 300);
        }}
        className="lg:w-[60vw] w-[95vw]"
      >
        <div className="flex flex-col gap-4 p-2">
          <div>
            <h1 className="font-bold text-lg text-slate-900 dark:text-white">
              {selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
            </h1>
            {selectedBerkas?.nomor && (
              <Badge className="mt-2 bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300">
                Nomor: {selectedBerkas?.nomor}
              </Badge>
            )}
          </div>

          {/* Panggil komponen PdfViewer */}
          {showPdf && selectedBerkas?.path_berkas && (
            <PdfViewer url={selectedBerkas.path_berkas} />
          )}
        </div>
      </Modal>
      <Modal
        show={showPortofolio}
        onClose={() => setShowPortofolio(false)}
        closeable={false}
        className="p-0 overflow-hidden flex flex-col h-[90vh] lg:w-[60vw] w-[95vw] bg-white dark:bg-slate-950"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between p-4 sm:px-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white leading-tight line-clamp-1">
                {selectedSysBerkasPortofolio?.nama_jenis_berkas ||
                  "Berkas Portofolio"}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Pratinjau Dokumen
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-300 rounded-full"
            onClick={() => setShowPortofolio(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* BODY (PDF VIEWER) */}
        <div className="flex-1 overflow-hidden bg-slate-100/50 dark:bg-slate-900/20 p-4 sm:p-6 relative">
          <div className="w-full h-full rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-inner bg-white dark:bg-slate-900 relative">
            <PdfViewer
              url={`${selectedSysBerkasPortofolio?.data_portofolio?.file_portofolio}`}
            />
          </div>
        </div>
      </Modal>
      <Modal
        show={showRapor}
        onClose={() => setShowRapor(false)}
        closeable={false}
        className="p-0 overflow-hidden flex flex-col h-[90vh] lg:w-[60vw] w-[95vw] bg-white dark:bg-slate-950"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between p-4 sm:px-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white leading-tight line-clamp-1">
                {selectedNilaiRapor?.kelas
                  ? `Kelas ${selectedNilaiRapor.kelas} Semester ${selectedNilaiRapor.semester}`
                  : "Nilai Rapor"}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Pratinjau Dokumen
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-300 rounded-full"
            onClick={() => setShowRapor(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* BODY (PDF VIEWER) */}
        <div className="flex-1 overflow-hidden bg-slate-100/50 dark:bg-slate-900/20 p-4 sm:p-6 relative">
          <div className="w-full h-full rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-inner bg-white dark:bg-slate-900 relative">
            <PdfViewer url={`${selectedNilaiRapor?.file_rapor_semester}`} />
          </div>
        </div>
      </Modal>

      <Modal
        show={showPrestasi}
        onClose={() => setShowPrestasi(false)}
        closeable={false}
        className="p-0 overflow-hidden flex flex-col h-[90vh] lg:w-[60vw] w-[95vw] bg-white dark:bg-slate-950"
      >
        {/* --- HEADER --- */}
        <div className="flex items-center justify-between p-4 sm:px-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white leading-tight line-clamp-1">
                {selectedDataPrestasi?.jenis_prestasi?.nama_jenis_prestasi ||
                  "Bukti Prestasi"}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Pratinjau Dokumen
              </p>
            </div>
          </div>

          {/* Tombol Silang (X) di Kanan Atas */}
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-300 rounded-full"
            onClick={() => setShowPrestasi(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* --- BODY (PDF VIEWER AREA) --- */}
        {/* flex-1 akan membuat area ini mengisi seluruh sisa ruang di tengah */}
        <div className="flex-1 overflow-hidden bg-slate-100/50 dark:bg-slate-900/20 p-4 sm:p-6 relative">
          <div className="w-full h-full rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-inner bg-white dark:bg-slate-900">
            <PdfViewer url={`${selectedDataPrestasi?.path_bukti_prestasi}`} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
